/**
 * The Log page's record of interview decisions.
 *
 * Two texts per decision, kept visually apart because they are not
 * interchangeable:
 *  - the INTERNAL summary (rating + what the interviewers said), which names
 *    shortfalls against the scorecard and must never reach a candidate;
 *  - the AGG-filtered candidate-facing paragraph, which is what a later "could
 *    I get more detailed feedback?" request is answered from.
 *
 * The second one has a copy button; the first deliberately does not.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { cn } from '../../../shared';
import { fetchDecisionLog, type DecisionLogEntry } from '../services/taOutboundApi';
import { isMine } from './logScope';

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-GB', {
        timeZone: 'Europe/Berlin',
        day: '2-digit',
        month: 'short',
        year: '2-digit',
      });
}

function DecisionRow({ entry }: { entry: DecisionLogEntry }) {
  const [open, setOpen] = useState(false);
  const rejected = entry.decision === 'rejected';

  const copyFeedback = () => {
    if (!entry.candidateFeedback) return;
    navigator.clipboard
      .writeText(entry.candidateFeedback)
      .then(() => showToast({ status: 'success', description: 'Candidate-safe feedback copied.' }))
      .catch(() => showToast({ status: 'error', description: 'Could not copy — select it manually.' }));
  };

  return (
    <li className="border-t border-gray-100 first:border-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full flex-wrap items-center gap-3 px-3 py-2 text-left hover:bg-gray-50"
        aria-expanded={open}
      >
        <span className="w-16 shrink-0 text-xs text-gray-400">{fmtWhen(entry.decidedAt)}</span>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">
          {entry.candidateName || entry.opportunityId}
        </span>
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold',
            rejected ? 'bg-[#FCDFE4] text-[#791F1F]' : 'bg-green-50 text-green-700'
          )}
        >
          {rejected ? `Rejected${entry.stageName ? ` · ${entry.stageName}` : ''}` : `→ ${entry.movedTo || 'next stage'}`}
        </span>
        {entry.rating && <span className="shrink-0 text-xs text-gray-500">{entry.rating}</span>}
        <span className="shrink-0 text-xs text-gray-300">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="space-y-3 bg-gray-50/60 px-3 pb-3 pt-1">
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Internal summary · not for the candidate
            </span>
            {entry.internalSummary.length > 0 ? (
              <ul className="mt-1 space-y-1">
                {entry.internalSummary.map((b, i) => (
                  <li key={i} className="text-sm leading-snug text-gray-700">
                    {b}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-sm italic text-gray-400">No summary was captured.</p>
            )}
            {entry.archiveReason && (
              <p className="mt-1 text-xs text-gray-500">Archive reason: {entry.archiveReason}</p>
            )}
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                Candidate-safe feedback · AGG-filtered
              </span>
              {entry.candidateFeedback && (
                <Button size="sm" variant="ghost" onClick={copyFeedback}>
                  Copy
                </Button>
              )}
            </div>
            {entry.candidateFeedback ? (
              <p className="mt-1 whitespace-pre-line rounded-md bg-white px-3 py-2 text-sm text-gray-700 ring-1 ring-gray-200">
                {entry.candidateFeedback}
              </p>
            ) : (
              <p className="mt-1 text-sm italic text-gray-400">
                None was drafted for this decision — write one from scratch if they ask.
              </p>
            )}
          </div>
          <p className="text-xs text-gray-400">Decided by {entry.decidedBy}</p>
        </div>
      )}
    </li>
  );
}

/** `mine` scopes the list to one recruiter's own decisions; null shows everyone's. */
export function DecisionLogPanel({ mine }: { mine?: string | null } = {}) {
  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['ta-decision-log'],
    queryFn: fetchDecisionLog,
  });
  const all = data ?? [];
  const rows = mine ? all.filter((e) => isMine(e.decidedBy, mine)) : all;

  return (
    <section className="mt-8">
      <header className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Interview decisions</h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            What was decided and why, with the candidate-safe feedback ready to reuse if they ask for
            detail. Kept for six months, then deleted.
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', isFetching && 'animate-spin')} />
          Refresh
        </Button>
      </header>

      {error && (
        <p className="rounded-lg border border-[#F7C0CA] bg-[#FCDFE4] px-3 py-2 text-sm text-[#791F1F]">
          {error instanceof Error ? error.message : 'Could not load the decision log.'}
        </p>
      )}

      {isPending ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
          {mine ? 'You have not recorded a decision yet.' : 'No decisions recorded yet.'}
        </p>
      ) : (
        <ul className="overflow-hidden rounded-xl ring-1 ring-gray-200">
          {rows.map((entry) => (
            <DecisionRow key={`${entry.opportunityId}-${entry.decidedAt}`} entry={entry} />
          ))}
        </ul>
      )}
    </section>
  );
}
